import React, { useMemo } from "react";
import PreIdVerification from "../../screens/auth/PreIdVerification";
import VStack from "../../ui_atoms/VStack";
import { MockedProvider } from "@apollo/client/testing";
import type { Meta, StoryObj } from "@storybook/react";
import { createStackNavigator } from "@react-navigation/stack";
import { OnboardingPermitType, onboardingPermitTypeAtom } from "../../atoms/auth";
import { ProfileType } from "../../__generated__/graphql";
import { currentLang } from "../../atoms/i18n";
import { RecoilRoot } from "recoil";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { buildUser } from "../factories";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

const Stack = createStackNavigator<{
    PreIdVerification: undefined;
}>();

type StoryComponentProps = {
    onboardingPermitType: OnboardingPermitType;
    profileType: ProfileType;
};

const StoryComponent = ({ onboardingPermitType, profileType }: StoryComponentProps) => {
    const user = useMemo(buildUser, []);
    return (
        <RecoilRoot
            initializeState={(snap) => {
                snap.set(currentLang, "fr");
                snap.set(onboardingPermitTypeAtom, onboardingPermitType);
            }}
        >
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager profileType={profileType}>
                    <MockedProvider mocks={[]}>
                        <VStack
                            gap={1}
                            style={{ alignItems: "stretch", alignSelf: "stretch", flex: 1 }}
                        >
                            <Stack.Navigator initialRouteName="PreIdVerification">
                                <Stack.Screen
                                    name="PreIdVerification"
                                    component={PreIdVerification}
                                    options={{ headerShown: false }}
                                />
                            </Stack.Navigator>
                        </VStack>
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </RecoilRoot>
    );
};

const meta = {
    title: "Screens/PreIdVerification",
    component: StoryComponent,
    argTypes: {
        profileType: {
            options: [ProfileType.Client, ProfileType.Worker],
            control: {
                type: "radio",
            },
        },
        onboardingPermitType: {
            options: ["swiss_or_permit", "no_permit"],
            control: {
                type: "radio",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        onboardingPermitType: "swiss_or_permit",
    },
};

export const WorkerSwiss: Story = {
    args: {
        profileType: ProfileType.Worker,
        onboardingPermitType: "swiss_or_permit",
    },
};

export const WorkerForeign: Story = {
    args: {
        profileType: ProfileType.Worker,
        onboardingPermitType: "no_permit",
    },
};
